import { useQuery } from '@apollo/client';
import {
  ActionGroup,
  PermissionToggle,
  Loader,
  ALL_SHARD_MODULES,
  DEFAULT_FIELDS_BY_COLUMN_NAME,
  getTranslatedText,
  getParsedCrudData
} from '@nile/components';
import { AccountClient, GET_FIELD } from '@nile/graphql';
import {
  Heading,
  Page,
  TextStyle,
  ResourceList,
  ResourceItem,
  InlineEdit,
  DisplayText
} from '@superops/ui-design-system';
import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import { SVGIcon } from '@superops/super-icons';
import { Tooltip } from 'react-clone-tippy';
import { isEmpty, map, orderBy, pick } from 'lodash';
import { useFlags } from 'launchdarkly-react-client-sdk';

const ImpactWrapper = styled.div`
  .heading {
    font-weight: 600;
    font-size: 20px;
    line-height: 21px;
    margin-bottom: 10px;
  }
  .description {
    font-size: 18px;
    line-height: 27px;
    margin-bottom: 20px;
  }

  .impact-wrapper {
    max-width: 400px;
  }
`;

const ImpactItem = styled.div`
  &.isEdit {
    padding: 6px 20px 12px;
  }
  &:hover {
    .pencil {
      visibility: visible;
    }
  }
`;

const TooltipMap = {
  0: 'admin.passwordPolicy.type.high',
  1: 'admin.passwordPolicy.type.medium',
  2: 'admin.passwordPolicy.type.low'
};

const ICON_MAP = {
  0: {
    color: '#D72C0D',
    iconName: 'UpArrow'
  },
  1: {
    color: '#FF7F23',
    iconName: 'UpArrow'
  },
  2: {
    color: '#00D247',
    iconName: 'DownArrow'
  }
};

const ImpactItemComponent = (props) => {
  const { index, intl } = props;
  const [edit, setEdit] = useState(false);
  const { item } = props;
  const id = item?.id;
  const value = item?.value;

  const handleFocusOut = (updatedText) => {
    props.handleFocusOut(item, updatedText);
    setEdit(false);
  };

  const getSVGIcon = (index) => {
    return ICON_MAP?.[index] || null;
  };

  const { iconName, color } = getSVGIcon(index);
  const tooltip = TooltipMap?.[index];
  const tooltipContent = `${getTranslatedText(
    intl,
    'ticket.search.impact'
  )}: ${getTranslatedText(intl, tooltip)}`;

  if (edit) {
    return (
      <ImpactItem className="fx actr isEdit g5" superid={`ImpactEdit-${id}`}>
        <div className="flex g5 actr">
          {iconName && (
            <div className="flex">
              <SVGIcon name={iconName} color={color} />
            </div>
          )}
          <div className="fx actr impact-list">
            <Heading element="h3">
              <InlineEdit
                text={value}
                labelClassName="InlineEditLabel"
                inputClassName="InlineEditInput"
                inputWidth="180px"
                inputHeight="25px"
                onFocus={() => {}}
                isEditing
                onFocusOut={handleFocusOut}
              />
            </Heading>
          </div>
        </div>
      </ImpactItem>
    );
  }

  return (
    <ResourceItem
      id={`impact${id}`}
      accessibilityLabel={`View details for ${value}`}
    >
      <ImpactItem className="fx actr hiddenContainer">
        <div className="flex g5 actr">
          {iconName && (
            <Tooltip
              title={tooltipContent}
              delay={200}
              duration={250}
              position="bottom"
              inertia
              trigger="mouseenter"
              animation="shift"
              animateFill={false}
            >
              <div className="flex pointer">
                <SVGIcon name={iconName} color={color} />
              </div>
            </Tooltip>
          )}
          <div className="fx actr impact-list">
            <DisplayText element="p" size="large" weight="Medium">
              <div className="flex actr">
                <div className="mr8">{value}</div>
                {props.isEditAuthorised && (
                  <div
                    className="hiddenFxActions"
                    onClick={(e) => {
                      setEdit(true);
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                  >
                    <SVGIcon name="Edit" />
                  </div>
                )}
              </div>
            </DisplayText>
          </div>
        </div>
      </ImpactItem>
    </ResourceItem>
  );
};

function Impact(props) {
  const { isEditAuthorised } = props;
  const intl = useIntl();
  const [impactData, setImpactData] = useState([]);
  const flags = useFlags();
  const [mandatoryOnCreation, setMandatoryOnCreation] = useState(false);
  const [mandatoryOnClosure, setMandatoryOnClosure] = useState(false);

  const { data, loading, error, refetch: refetchImpact } = useQuery(GET_FIELD, {
    client: AccountClient,
    fetchPolicy: 'network-only',
    variables: {
      input: {
        entity: ALL_SHARD_MODULES.ticket,
        columnName: DEFAULT_FIELDS_BY_COLUMN_NAME.IMPACT
      }
    },
    onCompleted({ getField }) {
      if (getField?.options || getField?.fieldOptions) {
        setImpactData(
          orderBy(getField?.options, 'order') || getField?.fieldOptions
        );
      }
      setMandatoryOnCreation(getField?.mandatoryOnCreate);
      setMandatoryOnClosure(getField?.mandatoryOnClosure);
    }
  });

  const updateImpact = (item, text) => {
    const base = [...impactData];
    let updatedImpacts = [];
    base.forEach((el) => {
      if (el?.id === item?.id) {
        const updatedData = {
          ...el,
          value: text
        };
        updatedImpacts = [...updatedImpacts, updatedData];
      } else {
        updatedImpacts = [...updatedImpacts, el];
      }
    });
    setImpactData(updatedImpacts);
  };

  function handleFocusOut(item, text) {
    const trimmedValue = text.trim();
    if (!trimmedValue) return;
    updateImpact(item, trimmedValue);
  }

  if (error) return <div>error occured </div>;

  const onSave = () => {
    const columnName = DEFAULT_FIELDS_BY_COLUMN_NAME.IMPACT;
    const baseData = data?.getField?.options || data?.getField?.fieldOptions;
    const updatedData = impactData;
    const result = getParsedCrudData(baseData, updatedData);
    let updatedOptions = {};
    const { updatedItems } = result;
    if (updatedItems.length > 0) {
      updatedOptions = {
        ...updatedOptions,
        updated: updatedItems,
        updateOptions: updatedItems
      };
    }

    let variables = {
      id: data?.getField?.id,
      mandatoryOnClosure: mandatoryOnClosure,
      mandatoryOnCreate: mandatoryOnCreation
    };

    if (flags?.dependentField) {
      variables = {
        ...variables,
        updateOptions: map(updatedOptions?.updateOptions, (e) =>
          pick(e, ['id', 'value', 'order'])
        )
      };
    } else {
      variables = {
        ...variables,
        fieldOptionsInput: isEmpty(updatedOptions)
          ? undefined
          : JSON.stringify(updatedOptions)
      };
    }
    props.primaryAction(variables);
  };

  return (
    <ImpactWrapper className="full-height full-width">
      <div className="full-height full-width">
        <Page
          variant="primary"
          contentHeight="140px"
          fullWidth
          title={
            <Heading element="h1">
              <FormattedMessage id="admin.mandatoryFields.edit.default.Fields" />
            </Heading>
          }
          primaryAction={
            <ActionGroup
              primaryText={<FormattedMessage id="common.save" />}
              primaryLoading={props.saveLoading}
              primaryAction={onSave}
              secondaryAction={props.secondaryAction}
              primaryDisabled={!isEditAuthorised}
            />
          }
        >
          <TextStyle variation="strong" weight="Bold">
            <p className="heading">
              <FormattedMessage id="admin.ImpactUrgencyPriority.impact" />
            </p>
          </TextStyle>
          <TextStyle variation="subdued">
            <p className="description">
              <FormattedMessage id="admin.ImpactUrgencyPriority.impact.desc" />
            </p>
          </TextStyle>

          {loading ? (
            <>
              <Loader />
            </>
          ) : (
            <>
              <div className="mb10 impact-wrapper">
                {impactData.map((item, index) => {
                  return (
                    <div key={item.id} className="flex g5 mb10">
                      <div>
                        <ImpactItemComponent
                          item={item}
                          handleFocusOut={handleFocusOut}
                          isEditAuthorised={isEditAuthorised}
                          index={index}
                          intl={intl}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mb40">
                <PermissionToggle
                  mandatoryOnCreation={mandatoryOnCreation}
                  setMandatoryOnCreation={setMandatoryOnCreation}
                  mandatoryOnClosure={mandatoryOnClosure}
                  setMandatoryOnClosure={setMandatoryOnClosure}
                  disableEdit={!isEditAuthorised}
                  showWarning={props.showWarning}
                  hideShowToClients
                />
              </div>
            </>
          )}
        </Page>
      </div>
    </ImpactWrapper>
  );
}

export default Impact;
